import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  CircleNotch,
  ListChecks,
  LockKey,
  Clock,
  ArrowsClockwise,
  CaretUp,
  CaretDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import {
  createTask,
  updateTask,
  parseTimeEstimate,
  formatTimeEstimate,
  PRIORITY_LABELS,
  STATUS_LABELS,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '@/lib/tasksApi';
import { fetchUsers } from '@/lib/usersApi';
import { fetchMatters } from '@/lib/mattersApi';
import { getApiErrorMessage } from '@/lib/api';
import { isMeaningfulTitle, TITLE_ERROR } from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  /** Pin the task to a matter when opened from a CaseDetailPage. */
  matterId?: string;
  /** Pin the task to a specific hearing (prep tasks opened from a HearingDetailPage). */
  hearingId?: string;
  /** Pin the assignee — useful for "assign to me" flows. */
  defaultAssigneeId?: string;
  /** Edit mode when an existing task is passed. */
  initial?: Task;
  onClose: () => void;
  onSaved: (t: Task) => void;
}

const PRIORITIES: TaskPriority[] = ['low', 'normal', 'high', 'urgent'];
const STATUSES: TaskStatus[] = ['pending', 'in_progress', 'blocked', 'completed', 'cancelled'];

export function TaskModal({ matterId, hearingId, defaultAssigneeId, initial, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const isEdit = Boolean(initial);

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [priority, setPriority] = useState<TaskPriority>(initial?.priority ?? 'normal');
  const [status, setStatus] = useState<TaskStatus>(initial?.status ?? 'pending');
  const [assignedToId, setAssignedToId] = useState<string | null>(
    initial?.assignedToId ?? defaultAssigneeId ?? null,
  );
  const [caseId, setCaseId] = useState<string | null>(initial?.caseId ?? matterId ?? null);
  const [dueDate, setDueDate] = useState(initial?.dueDate ? initial.dueDate.slice(0, 10) : '');
  const dueDateRef = useRef<HTMLInputElement>(null);
  // Single time-estimate field — accepts hours + minutes together ("1h 20m").
  const [timeEstInput, setTimeEstInput] = useState(formatTimeEstimate(initial?.estimatedMinutes ?? null));
  const [isPrivate, setIsPrivate] = useState(initial?.isPrivate ?? false);
  const [savedCount, setSavedCount] = useState(0);
  const [titleTouched, setTitleTouched] = useState(false);

  // The title was only checked for emptiness, inside the mutation, and reported as a toast —
  // so "1111111111111" saved happily. Checked here as the user types instead.
  //
  // On an EDIT the rule applies only once the title has changed. Titles stored before this
  // rule existed would otherwise disable Save outright, leaving the task uneditable — you
  // could not even change its status without first rewriting its title. `buildPayload` sends
  // the title only when it changed, so an untouched one never reaches the API either.
  const titleChanged = !isEdit || title.trim() !== (initial?.title ?? '').trim();
  const titleError = !title.trim()
    ? 'Title is required'
    : titleChanged && !isMeaningfulTitle(title)
      ? TITLE_ERROR
      : null;
  const showTitleError = titleTouched && titleError;

  const usersQ = useQuery({
    queryKey: ['users', 'firm-users-active'],
    queryFn: () => fetchUsers({ status: 'active', limit: 100 }),
  });

  const mattersQ = useQuery({
    queryKey: ['matters-picker-task'],
    queryFn: () => fetchMatters().then((r) => r.matters),
    enabled: !matterId,
    staleTime: 0,
  });

  // Reset form after a "save and create another" cycle.
  const resetForNext = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setTimeEstInput('');
    setIsPrivate(false);
    // Keep priority + assignee + matter — usually shared across the next task
  };

  const minutesParsed = timeEstInput ? parseTimeEstimate(timeEstInput) : null;
  const minutesValid = !timeEstInput || minutesParsed !== null;

  // Step the estimate up/down by 1 minute — 1m, 2m, 3m … 1h 1m, never jumps
  // straight to a whole hour.
  const stepEstimate = (deltaMin: number) => {
    const current = timeEstInput ? (parseTimeEstimate(timeEstInput) ?? 0) : 0;
    const next = Math.max(0, current + deltaMin);
    setTimeEstInput(next > 0 ? formatTimeEstimate(next) : '');
  };

  const buildPayload = () => {
    const payload: {
      title?: string;
      description?: string;
      priority: TaskPriority;
      status?: TaskStatus;
      assignedToId?: string;
      caseId?: string;
      hearingId?: string;
      dueDate?: string;
      estimatedMinutes?: number;
      isPrivate: boolean;
    } = {
      priority,
      isPrivate,
    };
    // Send the title on create always, and on edit only when it changed — so a task whose
    // stored title predates the current rule can still have its status or due date edited.
    if (titleChanged) payload.title = title.trim();
    const desc = description.trim();
    if (desc) payload.description = desc;
    if (assignedToId) payload.assignedToId = assignedToId;
    if (caseId) payload.caseId = caseId;
    // Prep tasks created from a hearing carry the hearing link so they surface on
    // the hearing's Preparation tab (only meaningful on create, not edit).
    if (!isEdit && hearingId) payload.hearingId = hearingId;
    if (dueDate) payload.dueDate = new Date(dueDate).toISOString();
    if (minutesParsed && minutesParsed > 0) payload.estimatedMinutes = minutesParsed;
    if (isEdit) payload.status = status; // status only editable on update
    return payload;
  };

  const saveMut = useMutation({
    mutationFn: async ({ andCreateAnother }: { andCreateAnother: boolean }) => {
      if (titleError) throw new Error(titleError);
      const payload = buildPayload();
      const result = isEdit && initial
        ? await updateTask(initial.id, payload)
        // On create the title is always present (`titleChanged` is true when !isEdit);
        // naming it here is what tells the type system so.
        : await createTask({ ...payload, title: title.trim() });
      return { task: result, andCreateAnother };
    },
    onSuccess: ({ task, andCreateAnother }) => {
      toast.success(isEdit ? 'Task updated' : 'Task created');
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['diary-tasks'] });
      const resolvedCaseId = task.caseId ?? caseId ?? matterId;
      if (resolvedCaseId) {
        qc.invalidateQueries({ queryKey: ['matter-tasks', resolvedCaseId] });
      }
      if (andCreateAnother && !isEdit) {
        setSavedCount((c) => c + 1);
        resetForNext();
      } else {
        onSaved(task);
      }
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save task')),
  });

  // Stop the page scrolling underneath: <ScrollLock /> inside the scrim below. It replaced
  // a local `overflow: hidden`, which iOS Safari ignores for touch scrolling.

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saveMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-full w-full max-w-2xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <ListChecks size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              {isEdit ? 'Edit task' : savedCount > 0 ? `New task (${savedCount} saved this session)` : 'New task'}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {isEdit ? 'Update the details below.' : 'Add a to-do for the chambers — assign it, schedule it, mark it private if needed.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !saveMut.isPending && onClose()}
            disabled={saveMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {!isEdit && hearingId && (
            <div className="rounded-md border border-brand-100 bg-brand-50 px-3 py-2 text-xs text-brand-800 dark:text-brand-200">
              This task will be linked to the hearing and shown on its Preparation tab.
            </div>
          )}
          {/* Title */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setTitleTouched(true)}
              placeholder="e.g. Draft skeleton argument by Friday"
              maxLength={500}
              disabled={saveMut.isPending}
              autoFocus
            />
            {showTitleError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{titleError}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Description <span className="font-normal normal-case text-muted-foreground/70">(optional · markdown supported)</span>
            </label>
            <ExpandableTextarea
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder={'Add detail, links, or **bold** the urgent bits.\n\n- Bullet points work\n- Like this'}
              rows={4}
              maxLength={5000}
              disabled={saveMut.isPending}
            />
          </div>

          {/* Priority + Status. Status exists only when editing, so on a NEW task the second
              column held nothing and left a blank half-row beside Priority. One column there. */}
          <div className={`grid gap-3 ${isEdit ? 'md:grid-cols-2' : ''}`}>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                disabled={saveMut.isPending}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </div>
            {isEdit && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TaskStatus)}
                  disabled={saveMut.isPending}
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Assignee + Matter */}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Assigned to <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              <select
                value={assignedToId ?? ''}
                onChange={(e) => setAssignedToId(e.target.value || null)}
                disabled={saveMut.isPending || usersQ.isLoading}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— Unassigned —</option>
                {usersQ.data?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </div>
            {!matterId && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  Matter <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
                </label>
                <select
                  value={caseId ?? ''}
                  onChange={(e) => setCaseId(e.target.value || null)}
                  disabled={saveMut.isPending || mattersQ.isLoading}
                  className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">— No matter —</option>
                  {mattersQ.data?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.number} · {m.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Due date + Time estimate */}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                Due date <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              {/* The calendar icon comes from globals.css, which paints one on every date and
                  time input in the app. This field used to draw its own on top of the native
                  button — the only field that did — which left two overlapping calendars once
                  the shared rule arrived. The shared rule keeps Chrome's native button in place
                  but invisible, directly under the icon, so clicking it still opens the picker. */}
              <Input
                ref={dueDateRef}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={saveMut.isPending}
              />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                <Clock size={11} weight="duotone" />
                Time estimate <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
              </label>
              {/* Single field — type hours + minutes together; steppers add/remove 5 min */}
              <div className="relative">
                <Input
                  value={timeEstInput}
                  onChange={(e) => setTimeEstInput(e.target.value)}
                  placeholder="e.g. 1h 20m, 45m, 1:30"
                  disabled={saveMut.isPending}
                  className="pr-8"
                />
                <div className="absolute right-1 top-1/2 flex -translate-y-1/2 flex-col">
                  <button type="button" title="Add 1 min" onClick={() => stepEstimate(1)} disabled={saveMut.isPending} className="flex h-3.5 items-center justify-center px-1 text-muted-foreground hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-50"><CaretUp size={10} weight="bold" /></button>
                  <button type="button" title="Subtract 1 min" onClick={() => stepEstimate(-1)} disabled={saveMut.isPending} className="flex h-3.5 items-center justify-center px-1 text-muted-foreground hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-50"><CaretDown size={10} weight="bold" /></button>
                </div>
              </div>
              {timeEstInput && !minutesValid && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">Could not parse — try "1h 20m", "45m", or "1:30".</p>
              )}
              {minutesParsed && minutesParsed > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Stored as <strong>{formatTimeEstimate(minutesParsed)}</strong> ({minutesParsed} minutes)
                </p>
              )}
            </div>
          </div>

          {/* Private toggle */}
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              disabled={saveMut.isPending}
              className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
            />
            <LockKey size={12} weight={isPrivate ? 'fill' : 'duotone'} className={isPrivate ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'} />
            <span className="font-medium">Private task</span>
            <span className="text-muted-foreground">— only visible to the assignee and you</span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <div className="text-xs text-muted-foreground">
            {savedCount > 0 && !isEdit && (
              <span>Last task saved · {savedCount} this session</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saveMut.isPending}>
              Cancel
            </Button>
            {!isEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => saveMut.mutate({ andCreateAnother: true })}
                disabled={saveMut.isPending || !!titleError || !minutesValid}
                className="gap-1.5"
              >
                {saveMut.isPending ? (
                  <CircleNotch size={14} className="animate-spin" />
                ) : (
                  <ArrowsClockwise size={13} weight="bold" />
                )}
                Save & create another
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={() => saveMut.mutate({ andCreateAnother: false })}
              disabled={saveMut.isPending || !!titleError || !minutesValid}
              className="gap-1.5"
            >
              {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
              {isEdit ? 'Save changes' : 'Save task'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
